// 004 (research R6, R16): which PinMap, geocoder and map settings the screens use. The default is
// the OSM provider with the configured tiles and geocoder `none`; tests inject FakePinMap.
import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { MapConfig } from "../../config/validate";
import { readPublicConfig } from "@/config";

import { createGeocoder } from "./geocoder";
import { OsmPinMap } from "./osm/OsmPinMap";
import type { Geocoder, PinMapComponent } from "./types";

export type MapServices = {
  PinMap: PinMapComponent;
  geocoder: Geocoder;
  config: MapConfig;
};

const FALLBACK: MapConfig = {
  provider: "osm",
  tileUrl: "",
  attribution: "© OpenStreetMap contributors",
  defaultCenter: { latitude: 17.4486, longitude: 78.3908, zoom: 13 },
  geocoder: "none",
};

function defaultServices(): MapServices {
  let config = FALLBACK;
  try {
    config = readPublicConfig().map;
  } catch {
    // RootShell already shows the config error screen; keep a harmless default here.
  }
  return {
    PinMap: OsmPinMap,
    geocoder: createGeocoder(config.geocoder),
    config,
  };
}

const MapContext = createContext<MapServices | null>(null);

export function MapProvider({
  value,
  children,
}: {
  value: MapServices;
  children: ReactNode;
}) {
  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

export function useMapServices(): MapServices {
  const injected = useContext(MapContext);
  return useMemo(() => injected ?? defaultServices(), [injected]);
}
